import type { OrderDetails } from '@/entities/order/model'
import { nanoid } from 'nanoid'
import { stateDefinitionConfigMock } from './definitions.mocks'

const projectId = nanoid()
const destinationId = '001'

// --- Canonical state names from config
const stateNames = stateDefinitionConfigMock.states.map((s: { name: string }) => s.name)
const DELIVERING = stateNames.find((s: string) => s === 'DeliveringToCustomer') || stateNames[0]
const ARRIVED = stateNames.find((s: string) => s === 'Arrived') || stateNames[1]
const ON_THE_WAY = stateNames.find((s: string) => s === 'OnTheWay') || stateNames[2]
const TEMPO_ON_THE_WAY = stateNames.find((s: string) => s === 'TempoOnTheWay') || stateNames[2]
const PARTNER_ON_THE_WAY = stateNames.find((s: string) => s === 'PartnerOnTheWay') || stateNames[2]
const READY_TO_PICK = stateNames.find((s: string) => s === 'ReadyToPick') || stateNames[3]
const READY_TO_COLLECT = stateNames.find((s: string) => s === 'ReadyToCollect') || stateNames[4]
const OVERDUE = stateNames.find((s: string) => s === 'OrderOverdue') || stateNames[5]

// --- Launch-relative datetime helpers ---
const now = Date.now()

function dt(offsetMinutes: number = 0) {
  return new Date(now + offsetMinutes * 60 * 1000).toISOString()
}

function collectionWindow(startOffset: number, endOffset: number) {
  const start = new Date(now + startOffset * 60 * 1000)
  const end = new Date(now + endOffset * 60 * 1000)
  return `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
}
// --- End helpers ---

/**
 * Mock OrderDetails object in "On the Way" state.
 *
 * This object is used in automated tests to simulate a real order.
 * It is intentionally simple and has incomplete data.
 * It is not meant to be used in production code.
 */
/**
 * Mock OrderDetails object in "Tempo On The Way" state.
 * This represents an order with an ETA from the Tempo delivery tracking system.
 */
export const mockTempoOnTheWayOrder: OrderDetails = {
  projectId,
  destinationId,
  installRef: nanoid(),
  helloId: nanoid(),
  status: TEMPO_ON_THE_WAY,
  eta: 5 * 60,
  etaDirection: 'lessThan',
  lastUpdateTime: dt(0),
  lastTransitionTime: dt(0),
  waitTimeStart: dt(0),
  customerName: 'Emma ETA',
  orderId: '100021',
  simphonyCheckNumber: 'no 21',
  isAcknowledged: false,
  hasNotified: [],
  orderActivationTime: dt(-2),
  hsFields: {
    'Customer Name': 'Emma ETA',
    'Order Number': '100021',
    'Collection Window': collectionWindow(-5, 10),
    'Mobile Number': '0424663111',
    'Car Model': 'Tesla',
    'Car Color': 'Red',
    'Car Plate': 'TESLA1',
    'Parking Bay': '5',
    'Van Seq': 'Van 11 Seq 111',
    'Ambient': '2',
    'chilled': '1',
    'Frozen': '0',
    'Order Type': 'Delivery',
    'Driver Name': 'Tom Tempo',
    'etaMinutes': '5',
    'arrivalTime': '11:05 AM',
    'etaDirectionPreposition': 'at',
  },
}

/**
 * Mock OrderDetails object in "Partner On The Way" state.
 * This represents an order with an ETA from a partner delivery service.
 */
export const mockPartnerOnTheWayOrder: OrderDetails = {
  projectId,
  destinationId,
  installRef: nanoid(),
  helloId: nanoid(),
  status: PARTNER_ON_THE_WAY,
  eta: 8 * 60,
  etaDirection: 'lessThan',
  lastUpdateTime: dt(2),
  lastTransitionTime: dt(2),
  waitTimeStart: dt(2),
  customerName: 'Liam Logistics',
  orderId: '100022',
  simphonyCheckNumber: 'no 22',
  isAcknowledged: false,
  hasNotified: [],
  orderActivationTime: dt(0),
  hsFields: {
    'Customer Name': 'Liam Logistics',
    'Order Number': '100022',
    'Collection Window': collectionWindow(-3, 12),
    'Mobile Number': '0424663112',
    'Car Model': 'Partner Van',
    'Car Color': 'Blue',
    'Car Plate': 'PRTNR1',
    'Parking Bay': '6',
    'Van Seq': 'Van 12 Seq 112',
    'ambient': '3',
    'Chilled': '2',
    'frozen': '1',
    'Order Type': 'Delivery',
    'Driver Name': 'Partner Driver',
    'etaMinutes': '8',
    'arrivalTime': '11:23 AM',
    'etaDirectionPreposition': 'around',
  },
}

/**
 * Mock OrderDetails object in "On The Way" state.
 */
export const mockOnTheWayOrder: OrderDetails = {
  projectId,
  destinationId,
  installRef: nanoid(),
  helloId: nanoid(),
  status: ON_THE_WAY,
  lastUpdateTime: dt(20),
  lastTransitionTime: dt(20),
  waitTimeStart: dt(20),
  customerName: 'Patty Planned',
  scheduledTimeForCollection: '2025-08-19T16:30:00.000Z', // Fixed time: 4:30 PM
  orderId: '100001',
  simphonyCheckNumber: 'no 7',
  isAcknowledged: false,
  hasNotified: [],
  orderActivationTime: dt(18),
  hsFields: {
    'Mobile Number': '0424663104',
    'Car Model': 'Flying Car',
    'Car Color': 'Invisible',
    'Car Plate': 'GH0ST77',
    'Parking Bay': '3',
    'Mobile Numbers': '0424663104',
    'Car Models': 'Flying Car',
    'Car Colors': 'Invisible',
    'Car Plates': 'GH0ST77',
    'Van Seq': 'Van 01 Seq 101',
    'orderType': 'Delivery',
    'Driver Name': 'Daniel Deliverer',
    'etaMinutes': '12',
    'arrivalTime': '09:42 AM',
    'etaDirectionPreposition': 'at',
    'Frozen': '1',
    'Restricted': '1',
    'Emojis': '🥶🆔',
  },
}

const mockOnTheWayOrder2: OrderDetails = {
  destinationId,
  projectId,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100002',
  customerName: 'Liam Late',
  hsFields: {
    'Customer Name': 'Liam Late',
    'Order Number': '100002',
    'Collection Window': collectionWindow(20, 40),
    'Mobile Number': '0424663104',
    'Car Model': 'Brumby',
    'Car Color': 'Green',
    'Car Plate': 'BRUMBY',
    'Parking Bay': '2',
    'Van Seq': 'Van 02 Seq 102',
    'Ambient': '1',
    'bulk ambient': '0',
    'Chilled': '2',
    'bulk chilled': '1',
    'Frozen': '2',
    'hot food': '1',
    'Restricted': 'No',
    'Order Type': 'Delivery',
    'Driver Name': 'Olivia OnTime',
    'Emojis': '📝⏰🚗🆔',
    'etaMinutes': '7',
    'arrivalTime': '09:52 AM',
    'etaDirectionPreposition': 'around',
  },
  eta: 7 * 60, // 7 minutes in seconds
  etaDirection: 'lessThan',
  status: ON_THE_WAY, // ensure always valid
  lastUpdateTime: dt(25),
  lastTransitionTime: dt(25),
  waitTimeStart: dt(25),
  orderActivationTime: dt(23),
  customEventMetaData: {
    array_barcodes: 'A100002*41*01,A100002*42*02',
  },
  isAcknowledged: false,
}

const mockOnTheWayOrder3: OrderDetails = {
  destinationId,
  projectId,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100003',
  customerName: 'Sophia Scheduled',
  hsFields: {
    'Customer Name': 'Sophia Scheduled',
    'Order Number': '100003',
    'Collection Window': collectionWindow(25, 45),
    'Mobile Number': '0424663104',
    'Car Model': 'Polaris',
    'Car Color': 'White',
    'Car Plate': 'POLARIS',
    'Parking Bay': '1',
    'Van Seq': 'Van 03 Seq 103',
    'Ambient': '3',
    'Bulk Ambient': '1',
    'Chilled': '2',
    'Bulk Chilled': '0',
    'Frozen': '1',
    'Hot Food': '0',
    'Restricted': 'Yes',
    'Order Type': 'Delivery',
    'Driver Name': 'William Wheels',
    'Emojis': '❄️🔥',
    'etaMinutes': '3',
    'arrivalTime': '10:03 AM',
    'etaDirectionPreposition': 'at',
  },
  eta: 3 * 60, // 3 minutes in seconds
  etaDirection: 'greaterThan',
  status: ON_THE_WAY, // ensure always valid
  lastUpdateTime: dt(30),
  lastTransitionTime: dt(30),
  waitTimeStart: dt(30),
  orderActivationTime: dt(28),
  isAcknowledged: false,
}

export const mockOnTheWayOrderWithETA: OrderDetails = {
  ...mockOnTheWayOrder,
  eta: 5 * 60, // 5 minutes
  etaDirection: 'lessThan',
  lastUpdateTime: dt(21),
}

export const mockArrivedOrder: OrderDetails = {
  destinationId,
  projectId,
  installRef: nanoid(),
  helloId: nanoid(),
  status: ARRIVED,
  scheduledTimeForCollection: dt(-8),
  lastUpdateTime: dt(-8),
  lastTransitionTime: dt(-8),
  waitTimeStart: dt(-8),
  customerName: 'Oliver Overdue',
  orderId: '100004',
  simphonyCheckNumber: 'no 7',
  isAcknowledged: false,
  hasNotified: [],
  orderActivationTime: dt(-10),
  hsFields: {
    'Mobile Number': '0424663104',
    'Car Model': 'Flying Car',
    'Car Color': 'Invisible',
    'Car Plate': 'GH0ST77',
    'Parking Bay': '3',
    'Mobile Numbers': '0424663104',
    'Car Models': 'Flying Car',
    'Car Colors': 'Invisible',
    'Car Plates': 'GH0ST77',
    'Van Seq': 'Van 01 Seq 101',
    'orderType': 'Delivery',
    'Driver Name': 'Daniel Deliverer',
    'etaMinutes': '12',
    'arrivalTime': '09:42 AM',
    'etaDirectionPreposition': 'at',
  },
}

export const mockArrivedOrder2: OrderDetails = {
  destinationId,
  projectId,
  installRef: nanoid(),
  helloId: nanoid(),
  orderId: '100005',
  customerName: 'Ava Arrived',
  hsFields: {
    'Customer Name': 'Ava Arrived',
    'Order Number': '100005',
    'Collection Window': collectionWindow(-12, -2),
    'Mobile Number': '0424663104',
    'Car Model': 'Honda',
    'Car Color': 'Silver',
    'Car Plate': 'RENU123',
    'Parking Bay': '4',
    'Van Seq': 'Van 04 Seq 104',
    'Ambient': '3',
    'Bulk Ambient': '0',
    'Chilled': '2',
    'Bulk Chilled': '1',
    'Frozen': '1',
    'Hot Food': '0',
    'Restricted': 'No',
    'Order Type': 'Delivery',
    'Driver Name': 'James Journey',
    'Emojis': '🆔🔪🍷',
  },
  status: ARRIVED, // ensure always valid
  isAcknowledged: false,
  hasNotified: [],
  lastUpdateTime: dt(-7),
  lastTransitionTime: dt(-7),
  waitTimeStart: dt(-7),
  orderActivationTime: dt(-9),
}

export const mockArrivedOrder3: OrderDetails = {
  destinationId,
  projectId,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100006',
  customerName: 'Noah Notified',
  hsFields: {
    'Customer Name': 'Noah Notified',
    'Order Number': '100006',
    'Collection Window': collectionWindow(-11, -1),
    'Mobile Number': '0424663105',
    'Car Model': 'Subaru',
    'Car Color': 'Blue',
    'Car Plate': 'SUTCHAI',
    'Parking Bay': '7',
    'Van Seq': 'Van 08 Seq 108',
    'Ambient': '1',
    'Bulk Ambient': '1',
    'Chilled': '2',
    'Bulk Chilled': '0',
    'Frozen': '3',
    'Hot Food': '1',
    'Emojis': '📝✅❄️',
    'Restricted': 'No',
    'Order Type': 'Delivery',
    'Driver Name': 'Amelia Arrives',
  },
  status: ARRIVED, // ensure always valid
  isAcknowledged: false,
  hasNotified: [],
  lastUpdateTime: dt(-6),
  lastTransitionTime: dt(-6),
  waitTimeStart: dt(-6),
  orderActivationTime: dt(-18),
}

export const mockDeliveringOrder: OrderDetails = {
  destinationId,
  projectId,
  installRef: nanoid(),
  helloId: nanoid(),
  status: DELIVERING,
  lastUpdateTime: dt(-15),
  lastTransitionTime: dt(-15),
  customerName: 'Isabella InTransit',
  orderId: '100009',
  simphonyCheckNumber: 'no 7',
  isAcknowledged: false,
  hasNotified: [],
  orderActivationTime: dt(-27),
  hsFields: {
    'Customer Name': 'Isabella InTransit',
    'Order Number': '100009',
    'Collection Window': '10:30 - 11:00',
    'Mobile Number': '0424663104',
    'Car Model': 'Flying Car',
    'Car Color': 'Invisible',
    'Car Plate': 'GH0ST77',
    'Parking Bay': '3',
    'Van Seq': 'Van 01 Seq 101',
    'Ambient': '1',
    'Bulk Ambient': '1',
    'Chilled': '3',
    'Bulk Chilled': '0',
    'Frozen': '2',
    'Hot Food': '1',
    'Restricted': 'No',
    'Order Type': 'Delivery',
    'Driver Name': 'Daniel Deliverer',
    'Emojis': '🆔🔪🍷',
  },
}

const mockDeliveringOrder2: OrderDetails = {
  destinationId,
  projectId,
  installRef: nanoid(),
  helloId: nanoid(),
  orderId: '100010',
  customerName: 'Mia MinutesAway',
  hsFields: {
    'Customer Name': 'Mia MinutesAway',
    'Order Number': '100010',
    'Collection Window': '10:45 - 11:15',
    'Mobile Number': '0424663104',
    'Car Model': 'Brumby',
    'Car Color': 'Green',
    'Car Plate': 'BRUMBY',
    'Parking Bay': '2',
    'Van Seq': 'Van 02 Seq 102',
    'Ambient': '2',
    'Bulk Ambient': '0',
    'Chilled': '2',
    'Bulk Chilled': '1',
    'Frozen': '2',
    'Hot Food': '1',
    'Restricted': 'No',
    'Order Type': 'Delivery',
    'Driver Name': 'Olivia OnTime',
    'Emojis': '🆔🔪🍷',
  },
  status: DELIVERING, // ensure always valid
  isAcknowledged: false,
  hasNotified: [],
  lastTransitionTime: '',
  lastUpdateTime: '',
  orderActivationTime: '',
}

const mockDeliveringOrder3: OrderDetails = {
  destinationId,
  projectId,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100011',
  customerName: 'Ethan EarlyBird',
  hsFields: {
    'Customer Name': 'Ethan EarlyBird',
    'Order Number': '100011',
    'Collection Window': '11:00 - 11:30',
    'Mobile Number': '0424663104',
    'Car Model': 'Polaris',
    'Car Color': 'White',
    'Car Plate': 'POLARIS',
    'Parking Bay': '1',
    'Van Seq': 'Van 03 Seq 103',
    'Ambient': '3',
    'Bulk Ambient': '1',
    'Chilled': '2',
    'Bulk Chilled': '0',
    'Frozen': '1',
    'Hot Food': '0',
    'Restricted': 'Yes',
    'Order Type': 'Delivery',
    'Driver Name': 'William Wheels',
    'Emojis': '🆔🔪🍷',
  },
  status: DELIVERING, // ensure always valid
  isAcknowledged: false,
  hasNotified: [],
  lastTransitionTime: '',
  lastUpdateTime: '',
  orderActivationTime: '',
}

export const mockCollectionCodeDeliveringOrder: OrderDetails = {
  ...mockDeliveringOrder,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100120',
  customerName: 'Casey Collect',
  hsFields: {
    ...mockDeliveringOrder.hsFields,
    'Order Number': '100120',
    'Order Type': 'Click & Collect',
    'Car Plate': 'CLCT120',
    'Van Seq': 'Van 20 Seq 120',
  },
}

export const mockCollectionCodeRapidDeliveringOrder: OrderDetails = {
  ...mockDeliveringOrder,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100121',
  customerName: 'Rory Rapid Collect',
  hsFields: {
    ...mockDeliveringOrder.hsFields,
    'Order Number': '100121',
    'Order Type': 'Click & Collect Rapid',
    'Car Plate': 'RAPD121',
    'Van Seq': 'Van 21 Seq 121',
  },
}

export const mockReadyToPickOrder: OrderDetails = {
  destinationId,
  projectId,
  installRef: nanoid(),
  helloId: nanoid(),
  status: READY_TO_PICK,
  lastUpdateTime: dt(-10),
  lastTransitionTime: dt(-10),
  customerName: 'Pickle Rick',
  orderId: '100012',
  simphonyCheckNumber: 'no 8',
  isAcknowledged: false,
  hasNotified: [],
  orderActivationTime: dt(-12),
  hsFields: {
    'Customer Name': 'Pickle Rick',
    'Order Number': '100012',
    'Collection Window': '11:00 - 11:30',
    'Mobile Number': '0424663105',
    'Car Model': 'Toyota',
    'Car Color': 'Blue',
    'Car Plate': 'PICK123',
    'Parking Bay': '4',
    'Van Seq': 'Van 05 Seq 105',
    'Ambient': '4',
    'Bulk Ambient': '1',
    'Chilled': '2',
    'Bulk Chilled': '0',
    'Frozen': '1',
    'Hot Food': '0',
    'Restricted': 'No',
    'Order Type': 'Pickup',
    'Driver Name': 'Lucas Logistics',
  },
}

export const mockReadyToPickOrder2: OrderDetails = {
  ...mockReadyToPickOrder,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100015',
  customerName: 'Pickford Mason',
  hsFields: {
    ...mockReadyToPickOrder.hsFields,
    'Car Plate': 'PICK456',
    'Parking Bay': '7',
    'Van Seq': 'Van 08 Seq 108',
    'orderType': 'Pickup',
    'Driver Name': 'Pickford Driver',
  },
  isAcknowledged: false,
  hasNotified: [],
}

const mockReadyToPickOrder3: OrderDetails = {
  ...mockReadyToPickOrder,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100016',
  customerName: 'Ricky Picker',
  hsFields: {
    ...mockReadyToPickOrder.hsFields,
    'Car Plate': 'PICK789',
    'Parking Bay': '8',
    'Ambient': '2',
    'Bulk Ambient': '0',
    'Chilled': '3',
    'Bulk Chilled': '1',
    'Frozen': '0',
    'Hot Food': '0',
    'Restricted': 'No',
    'Order Type': 'Pickup',
    'Van Seq': 'Van 09 Seq 109',
    'orderType': 'Pickup',
    'Driver Name': 'Ricky Driver',
  },
  isAcknowledged: false,
  hasNotified: [],
}

export const mockReadyToCollectOrder: OrderDetails = {
  destinationId,
  projectId,
  installRef: nanoid(),
  helloId: nanoid(),
  status: READY_TO_COLLECT,
  lastUpdateTime: dt(-10),
  lastTransitionTime: dt(-10),
  customerName: 'Collette Ready',
  orderId: '100013',
  simphonyCheckNumber: 'no 9',
  isAcknowledged: false,
  hasNotified: [],
  orderActivationTime: dt(-12),
  hsFields: {
    'Customer Name': 'Collette Ready',
    'Order Number': '100013',
    'Collection Window': '11:30 - 12:00',
    'Mobile Number': '0424663106',
    'Car Model': 'Hyundai',
    'Car Color': 'Red',
    'Car Plate': 'COLL321',
    'Parking Bay': '5',
    'Van Seq': 'Van 06 Seq 106',
    'Ambient': '2',
    'Bulk Ambient': '0',
    'Chilled': '3',
    'Bulk Chilled': '1',
    'Frozen': '2',
    'Hot Food': '0',
    'Restricted': 'No',
    'Order Type': 'Collection',
    'Driver Name': 'Collector Driver',
  },
}

export const mockReadyToCollectOrder2: OrderDetails = {
  ...mockReadyToCollectOrder,
  destinationId,
  projectId,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100017',
  customerName: 'Colin Collector',
  hsFields: {
    ...mockReadyToCollectOrder.hsFields,
    'Car Plate': 'COLL654',
    'Parking Bay': '9',
    'Van Seq': 'Van 10 Seq 110',
    'orderType': 'Collection',
    'Driver Name': 'Colin Driver',
  },
}

const mockReadyToCollectOrder3: OrderDetails = {
  ...mockReadyToCollectOrder,
  destinationId,
  projectId,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100018',
  customerName: 'Reddy Collette',
  hsFields: {
    ...mockReadyToCollectOrder.hsFields,
    'Car Plate': 'COLL987',
    'Parking Bay': '10',
    'Van Seq': 'Van 11 Seq 111',
    'orderType': 'Collection',
    'Driver Name': 'Reddy Driver',
  },
}

export const mockOverdueOrder: OrderDetails = {
  destinationId,
  projectId,
  installRef: nanoid(),
  helloId: nanoid(),
  status: OVERDUE,
  lastUpdateTime: dt(-30),
  lastTransitionTime: dt(-30),
  waitTimeStart: dt(-30),
  customerName: 'Olivia Overdue',
  orderId: '100014',
  simphonyCheckNumber: 'no 10',
  isAcknowledged: false,
  hasNotified: [],
  orderActivationTime: dt(-32),
  hsFields: {
    'Customer Name': 'Olivia Overdue',
    'Order Number': '100014',
    'Collection Window': '09:00 - 09:30',
    'Mobile Number': '0424663107',
    'Car Model': 'Mazda',
    'Car Color': 'Black',
    'Car Plate': 'OVER999',
    'Parking Bay': '6',
    'Van Seq': 'Van 07 Seq 107',
    'Ambient': '3',
    'Bulk Ambient': '1',
    'Chilled': '2',
    'Bulk Chilled': '1',
    'Frozen': '1',
    'Hot Food': '1',
    'Restricted': 'Yes',
    'Order Type': 'Overdue',
    'Driver Name': 'Overdue Driver',
  },
}

const mockOverdueOrder2: OrderDetails = {
  ...mockOverdueOrder,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100019',
  customerName: 'Omar Overdue',
  lastUpdateTime: dt(-40),
  lastTransitionTime: dt(-40),
  waitTimeStart: dt(-40),
  hsFields: {
    ...mockOverdueOrder.hsFields,
    'Customer Name': 'Omar Overdue',
    'Order Number': '100019',
    'Collection Window': '08:45 - 09:15',
    'Car Plate': 'OVER888',
    'Parking Bay': '11',
    'Ambient': '1',
    'Bulk Ambient': '1',
    'Chilled': '2',
    'Bulk Chilled': '0',
    'Frozen': '2',
    'Hot Food': '0',
    'Restricted': 'No',
    'Order Type': 'Overdue',
    'Van Seq': 'Van 12 Seq 112',
    'orderType': 'Overdue',
    'Driver Name': 'Omar Driver',
  },
}

const mockOverdueOrder3: OrderDetails = {
  ...mockOverdueOrder,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100020',
  customerName: 'Odessa Overdue',
  lastUpdateTime: dt(-50),
  lastTransitionTime: dt(-50),
  waitTimeStart: dt(-50),
  hsFields: {
    ...mockOverdueOrder.hsFields,
    'Customer Name': 'Odessa Overdue',
    'Order Number': '100020',
    'Collection Window': '08:30 - 09:00',
    'Car Plate': 'OVER777',
    'Parking Bay': '12',
    'Ambient': '2',
    'Bulk Ambient': '0',
    'Chilled': '1',
    'Bulk Chilled': '1',
    'Frozen': '3',
    'Hot Food': '0',
    'Restricted': 'Yes',
    'Order Type': 'Overdue',
    'Van Seq': 'Van 13 Seq 113',
    'orderType': 'Overdue',
    'Driver Name': 'Odessa Driver',
  },
}

export const mockOverdueOrder4: OrderDetails = {
  ...mockOverdueOrder,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100021',
  customerName: 'Oscar Overdue',
  lastUpdateTime: dt(-60),
  lastTransitionTime: dt(-60),
  waitTimeStart: dt(-60),
  hsFields: {
    ...mockOverdueOrder.hsFields,
    'Customer Name': 'Oscar Overdue',
    'Order Number': '100021',
    'Collection Window': '08:15 - 08:45',
    'Car Plate': 'OVER666',
    'Parking Bay': '13',
    'Ambient': '2',
    'Bulk Ambient': '1',
    'Chilled': '3',
    'Bulk Chilled': '0',
    'Frozen': '2',
    'Hot Food': '1',
    'Restricted': 'No',
    'Order Type': 'Overdue',
    'Van Seq': 'Van 14 Seq 114',
    'orderType': 'Overdue',
    'Driver Name': 'Oscar Driver',
  },
}

// Partner orders with exact same pickup time (for backward compatibility testing)
export const mockGroupPartnerOnTheWayOrders: OrderDetails[] = [
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: PARTNER_ON_THE_WAY,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(),
    customerName: 'Marcus Martinez',
    orderId: '100070',
    simphonyCheckNumber: 'no 70',
    isAcknowledged: false,
    hasNotified: [],
    orderActivationTime: dt(-5),
    partnerPickupTime: dt(-15),
    hsFields: {
      'Customer Name': 'Marcus Martinez',
      'Order Number': '100070',
      'Collection Window': '14:30 - 15:00',
      'Mobile Number': '0455887799',
      'Car Model': 'Partner Delivery Van',
      'Car Color': 'Silver',
      'Car Plate': 'DLVR888',
      'Parking Bay': '15',
      'Van Seq': 'Van 05 Seq 505',
      'Ambient': '4',
      'Chilled': '3',
      'Frozen': '2',
      'Order Type': 'Partner Delivery',
      'Driver Name': 'Alex Partner',
      'etaMinutes': '15',
      'arrivalTime': '14:45 PM',
      'customerName': 'Logan Logistics',
      'orderId': '100022',
      'simphonyCheckNumber': 'no 22',
    },
  },
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: PARTNER_ON_THE_WAY,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(),
    customerName: 'Marcus Martinez',
    orderId: '100071',
    simphonyCheckNumber: 'no 71',
    isAcknowledged: false,
    hasNotified: [],
    orderActivationTime: dt(-5),
    partnerPickupTime: dt(-15),
    hsFields: {
      'Customer Name': 'Marcus Martinez 2',
      'Order Number': '100070',
      'Collection Window': '14:30 - 15:00',
      'Mobile Number': '0455887799',
      'Car Model': 'Partner Delivery Van',
      'Car Color': 'Silver',
      'Car Plate': 'DLVR888',
      'Parking Bay': '15',
      'Van Seq': 'Van 05 Seq 505',
      'Ambient': '4',
      'Chilled': '3',
      'Frozen': '2',
      'Order Type': 'Partner Delivery',
      'Driver Name': 'Alex Partner',
      'etaMinutes': '15',
      'arrivalTime': '14:45 PM',
      'customerName': 'Logan Logistics',
      'orderId': '100022',
      'simphonyCheckNumber': 'no 22',
    },
  },
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: PARTNER_ON_THE_WAY,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(),
    customerName: 'Felicity Fiat',
    orderId: '100023',
    simphonyCheckNumber: 'no 23',
    isAcknowledged: false,
    hasNotified: [],
    orderActivationTime: dt(-2),
    eta: 12 * 60,
    etaDirection: 'greaterThan',
    hsFields: {
      'Order Type': 'Partner Delivery',
      'Customer Name': 'Felicity Fiat',
      'Order Number': '100023',
      'Collection Window': '11:15 - 11:45',
      'Mobile Number': '0424663112',
      'Car Model': 'Partner Van',
      'Car Color': 'Blue',
      'Car Plate': 'PRTNR1',
      'Parking Bay': '6',
      'Van Seq': 'Van 12 Seq 112',
      'Ambient': '3',
      'Chilled': '2',
      'Frozen': '1',
      'Driver Name': 'Jordan Journey',
      'etaMinutes': '8',
      'arrivalTime': '11:23 AM',
      'etaDirectionPreposition': 'around',
    },
  },
]

// Partner orders with pickup times within 15 minutes (should be grouped)
export const mockGroupPartnerOrdersWithin15Min: OrderDetails[] = [
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: PARTNER_ON_THE_WAY,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(),
    customerName: 'GROUP Sarah Smith',
    orderId: '100080',
    simphonyCheckNumber: 'no 80',
    isAcknowledged: false,
    hasNotified: [],
    orderActivationTime: dt(-5),
    // Base pickup time
    partnerPickupTime: '2025-10-16T14:00:00.000Z',
    hsFields: {
      'Customer Name': 'Sarah Smith',
      'Order Number': '100080',
      'Collection Window': '14:00 - 14:30',
      'Mobile Number': '0455112233',
      'Car Model': 'Partner Delivery Van',
      'Car Color': 'White',
      'Car Plate': 'PTN001',
      'Parking Bay': '20',
      'Van Seq': 'Van 20 Seq 520',
      'Ambient': '3',
      'Chilled': '2',
      'Frozen': '1',
      'Order Type': 'Partner Delivery',
      'Driver Name': 'Mike Partner',
      'etaMinutes': '10',
      'arrivalTime': '14:10 PM',
    },
  },
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: PARTNER_ON_THE_WAY,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(),
    customerName: 'GROUP John Davis',
    orderId: '100081',
    simphonyCheckNumber: 'no 81',
    isAcknowledged: false,
    hasNotified: [],
    orderActivationTime: dt(-5),
    // 10 minutes after first order (within 15-min threshold)
    partnerPickupTime: '2025-10-16T14:10:00.000Z',
    hsFields: {
      'Customer Name': 'John Davis',
      'Order Number': '100081',
      'Collection Window': '14:10 - 14:40',
      'Mobile Number': '0455112244',
      'Car Model': 'Partner Delivery Van',
      'Car Color': 'White',
      'Car Plate': 'PTN001',
      'Parking Bay': '20',
      'Van Seq': 'Van 20 Seq 521',
      'Ambient': '2',
      'Chilled': '3',
      'Frozen': '1',
      'Order Type': 'Partner Delivery',
      'Driver Name': 'Mike Partner',
      'etaMinutes': '10',
      'arrivalTime': '14:20 PM',
    },
  },
]

// Partner orders with pickup times beyond 15 minutes (should not be grouped)
export const mockNotGroupPartnerOrdersBeyond15Min: OrderDetails[] = [
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: PARTNER_ON_THE_WAY,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(),
    customerName: 'NOT GROUP Emily Johnson',
    orderId: '100085',
    simphonyCheckNumber: 'no 85',
    isAcknowledged: false,
    hasNotified: [],
    orderActivationTime: dt(-5),
    // Base pickup time
    partnerPickupTime: '2025-10-16T15:00:00.000Z',
    hsFields: {
      'Customer Name': 'Emily Johnson',
      'Order Number': '100085',
      'Collection Window': '15:00 - 15:30',
      'Mobile Number': '0455223344',
      'Car Model': 'Partner Delivery Van',
      'Car Color': 'Black',
      'Car Plate': 'PTN002',
      'Parking Bay': '21',
      'Van Seq': 'Van 21 Seq 525',
      'Ambient': '4',
      'Chilled': '3',
      'Frozen': '2',
      'Order Type': 'Partner Delivery',
      'Driver Name': 'David Partner',
      'etaMinutes': '12',
      'arrivalTime': '15:12 PM',
    },
  },
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: PARTNER_ON_THE_WAY,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(),
    customerName: 'NOT GROUP Michael Brown',
    orderId: '100086',
    simphonyCheckNumber: 'no 86',
    isAcknowledged: false,
    hasNotified: [],
    orderActivationTime: dt(-5),
    // 16 minutes after first order (beyond 15-min threshold)
    partnerPickupTime: '2025-10-16T15:16:00.000Z',
    hsFields: {
      'Customer Name': 'Michael Brown',
      'Order Number': '100086',
      'Collection Window': '15:15 - 15:45',
      'Mobile Number': '0455223355',
      'Car Model': 'Partner Delivery Van',
      'Car Color': 'Black',
      'Car Plate': 'PTN002',
      'Parking Bay': '21',
      'Van Seq': 'Van 21 Seq 526',
      'Ambient': '3',
      'Chilled': '2',
      'Frozen': '1',
      'Order Type': 'Partner Delivery',
      'Driver Name': 'David Partner',
      'etaMinutes': '12',
      'arrivalTime': '15:28 PM',
    },
  },
]

// Partner orders with pickup times exactly 15 minutes apart (should be grouped)
export const mockGroupPartnerOrdersExactly15Min: OrderDetails[] = [
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: PARTNER_ON_THE_WAY,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(),
    customerName: 'GROUP Robert Wilson',
    orderId: '100090',
    simphonyCheckNumber: 'no 90',
    isAcknowledged: false,
    hasNotified: [],
    orderActivationTime: dt(-5),
    // Base pickup time
    partnerPickupTime: '2025-10-16T16:00:00.000Z',
    hsFields: {
      'Customer Name': 'Robert Wilson',
      'Order Number': '100090',
      'Collection Window': '16:00 - 16:30',
      'Mobile Number': '0455334455',
      'Car Model': 'Partner Delivery Van',
      'Car Color': 'Blue',
      'Car Plate': 'PTN003',
      'Parking Bay': '22',
      'Van Seq': 'Van 22 Seq 530',
      'Ambient': '2',
      'Chilled': '3',
      'Frozen': '1',
      'Order Type': 'Partner Delivery',
      'Driver Name': 'Lisa Partner',
      'etaMinutes': '8',
      'arrivalTime': '16:08 PM',
    },
  },
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: PARTNER_ON_THE_WAY,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(),
    customerName: 'GROUP Jennifer Taylor',
    orderId: '100091',
    simphonyCheckNumber: 'no 91',
    isAcknowledged: false,
    hasNotified: [],
    orderActivationTime: dt(-5),
    // Exactly 15 minutes after first order (at the threshold, should group)
    partnerPickupTime: '2025-10-16T16:15:00.000Z',
    hsFields: {
      'Customer Name': 'Jennifer Taylor',
      'Order Number': '100091',
      'Collection Window': '16:15 - 16:45',
      'Mobile Number': '0455334466',
      'Car Model': 'Partner Delivery Van',
      'Car Color': 'Blue',
      'Car Plate': 'PTN003',
      'Parking Bay': '22',
      'Van Seq': 'Van 22 Seq 531',
      'Ambient': '3',
      'Chilled': '2',
      'Frozen': '2',
      'Order Type': 'Partner Delivery',
      'Driver Name': 'Lisa Partner',
      'etaMinutes': '10',
      'arrivalTime': '16:25 PM',
    },
  },
]

export const mockGroupArrivedOrders: OrderDetails[] = [
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: ARRIVED,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(-15),
    customerName: 'Emma Ewing',
    orderId: '100024',
    simphonyCheckNumber: 'no 24',
    isAcknowledged: false,
    hasNotified: ['consolidation'],
    orderActivationTime: dt(-2),
    hsFields: {
      'orderGroupId': 'arrived123',
      'Customer Name': 'Emma Ewing',
      'Order Number': '100024',
      'Collection Window': '10:30 - 11:00',
      'Mobile Number': '0424555666',
      'Car Model': 'Arrival Van',
      'Car Color': 'White',
      'Car Plate': 'ARR123',
      'Parking Bay': '7',
      'Van Seq': 'Van 14 Seq 114',
      'Ambient': '4',
      'Chilled': '3',
      'Frozen': '1',
      'Order Type': 'Delivery',
      'Driver Name': 'Alex Arrival',
      'arrivalTime': 'Arrived',
      'isGroupOrder': 'true',
      'groupOrderId': 'GRP-ARR-001',
      'groupOrderName': 'Morning Arrival Group',
    },
  },
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: ARRIVED,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(-5),
    customerName: 'Kelsey Kargo',
    orderId: '100025',
    simphonyCheckNumber: 'no 25',
    isAcknowledged: true,
    hasNotified: ['consolidation'],
    orderActivationTime: dt(-2),
    hsFields: {
      'orderGroupId': 'arrived123',
      'Customer Name': 'Kelsey Kargo',
      'Order Number': '100025',
      'Collection Window': '10:30 - 11:00',
      'Mobile Number': '0424666777',
      'Car Model': 'Arrival Van',
      'Car Color': 'White',
      'Car Plate': 'ARR123',
      'Parking Bay': '7',
      'Van Seq': 'Van 14 Seq 115',
      'Ambient': '2',
      'Chilled': '4',
      'Frozen': '0',
      'Order Type': 'Delivery',
      'Driver Name': 'Alex Arrival',
      'arrivalTime': 'Arrived',
      'isGroupOrder': 'true',
      'groupOrderId': 'GRP-ARR-001',
      'groupOrderName': 'Morning Arrival Group',
    },
  },
]

export const mockGroupDeliveringOrders: OrderDetails[] = [
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: DELIVERING,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(-20),
    customerName: 'Ruby Ready',
    orderId: '100026',
    simphonyCheckNumber: 'no 26',
    isAcknowledged: true,
    hasNotified: ['consolidation'],
    orderActivationTime: dt(-2),
    hsFields: {
      'orderGroupId': 'delivering123',
      'Customer Name': 'Ruby Ready',
      'Order Number': '100026',
      'Collection Window': '13:00 - 13:30',
      'Mobile Number': '0424777888',
      'Car Model': 'Delivery Van',
      'Car Color': 'Red',
      'Car Plate': 'DEL456',
      'Parking Bay': '8',
      'Van Seq': 'Van 15 Seq 116',
      'Ambient': '5',
      'Chilled': '3',
      'Frozen': '2',
      'Order Type': 'Delivery',
      'Driver Name': 'Liam Driver',
      'arrivalTime': 'In Progress',
      'isGroupOrder': 'true',
      'groupOrderId': 'GRP-DEL-001',
      'groupOrderName': 'Lunchtime Delivery Group',
    },
  },
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: DELIVERING,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(-18),
    customerName: 'William Waterfall',
    orderId: '100027',
    simphonyCheckNumber: 'no 27',
    isAcknowledged: false,
    hasNotified: ['consolidation'],
    orderActivationTime: dt(-2),
    hsFields: {
      'orderGroupId': 'delivering123',
      'Customer Name': 'William Waterfall',
      'Order Number': '100027',
      'Collection Window': '13:00 - 13:30',
      'Mobile Number': '0424888999',
      'Car Model': 'Delivery Van',
      'Car Color': 'Red',
      'Car Plate': 'DEL456',
      'Parking Bay': '8',
      'Van Seq': 'Van 15 Seq 117',
      'Ambient': '3',
      'Chilled': '4',
      'Frozen': '1',
      'Order Type': 'Delivery',
      'Driver Name': 'Liam Driver',
      'arrivalTime': 'In Progress',
      'isGroupOrder': 'true',
      'groupOrderId': 'GRP-DEL-001',
      'groupOrderName': 'Lunchtime Delivery Group',
    },
  },
]

export const mockGroupOverdueOrders: OrderDetails[] = [
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: OVERDUE,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(-90),
    customerName: 'Ava Overdue',
    orderId: '100028',
    simphonyCheckNumber: 'no 28',
    isAcknowledged: false,
    hasNotified: ['consolidation'],
    orderActivationTime: dt(-2),
    hsFields: {
      'orderGroupId': 'overdue123',
      'Customer Name': 'Ava Overdue',
      'Order Number': '100028',
      'Collection Window': '09:00 - 09:30',
      'Mobile Number': '0424999000',
      'Car Model': 'Overdue Van',
      'Car Color': 'Yellow',
      'Car Plate': 'OVD789',
      'Parking Bay': '9',
      'Van Seq': 'Van 16 Seq 118',
      'Ambient': '6',
      'Chilled': '2',
      'Frozen': '3',
      'Order Type': 'Overdue',
      'Driver Name': 'James Overdue',
      'isGroupOrder': 'true',
      'groupOrderId': 'GRP-OVD-001',
      'groupOrderName': 'Morning Overdue Group',
    },
  },
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: OVERDUE,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(-85),
    customerName: 'Ethan Overdue',
    orderId: '100029',
    simphonyCheckNumber: 'no 29',
    isAcknowledged: false,
    hasNotified: ['consolidation'],
    orderActivationTime: dt(-2),
    hsFields: {
      'orderGroupId': 'overdue123',
      'Customer Name': 'Ethan Overdue',
      'Order Number': '100029',
      'Collection Window': '09:00 - 09:30',
      'Mobile Number': '0425000111',
      'Car Model': 'Overdue Van',
      'Car Color': 'Yellow',
      'Car Plate': 'OVD789',
      'Parking Bay': '9',
      'Van Seq': 'Van 16 Seq 119',
      'Ambient': '4',
      'Chilled': '3',
      'Frozen': '2',
      'Order Type': 'Overdue',
      'Driver Name': 'James Overdue',
      'isGroupOrder': 'true',
      'groupOrderId': 'GRP-OVD-001',
      'groupOrderName': 'Morning Overdue Group',
    },
  },
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: OVERDUE,
    lastUpdateTime: dt(),
    lastTransitionTime: dt(),
    waitTimeStart: dt(-80),
    customerName: 'Victoria Volcano',
    orderId: '100021',
    simphonyCheckNumber: 'no 21',
    isAcknowledged: false,
    hasNotified: ['consolidation'],
    orderActivationTime: dt(-2),
    hsFields: {
      'orderGroupId': 'overdue123',
      'Customer Name': 'Victoria Volcano',
      'Order Number': '100021',
      'Collection Window': '08:15 - 08:45',
      'Mobile Number': '0425000222',
      'Car Model': 'Overdue Sedan',
      'Car Color': 'Black',
      'Car Plate': 'OVD666',
      'Parking Bay': '10',
      'Van Seq': 'Van 17 Seq 120',
      'Ambient': '5',
      'Chilled': '3',
      'Frozen': '2',
      'Order Type': 'Overdue',
      'Driver Name': 'Charlie Courier',
      'isGroupOrder': 'true',
      'groupOrderId': 'GRP-OVD-002',
      'groupOrderName': 'Afternoon Overdue Group',
    },
  },
]

export const mockGroupReadyToCollectOrders: OrderDetails[] = [
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: READY_TO_COLLECT,
    lastUpdateTime: dt(-5),
    lastTransitionTime: dt(-5),
    customerName: 'Ready Group 1',
    orderId: '100090',
    simphonyCheckNumber: 'no 90',
    isAcknowledged: false,
    hasNotified: [],
    orderActivationTime: dt(-10),
    partnerPickupTime: '2025-10-16T15:00:00.000Z',
    hsFields: {
      'Customer Name': 'Ready Group 1',
      'Order Number': '100090',
      'Collection Window': '15:00 - 15:30',
      'Mobile Number': '0424555001',
      'Car Model': 'Partner Van',
      'Car Color': 'White',
      'Car Plate': 'RDY001',
      'Parking Bay': '15',
      'Van Seq': 'Van 15 Seq 515',
      'Ambient': '2',
      'Chilled': '1',
      'Frozen': '1',
      'Order Type': 'Partner Delivery',
      'Driver Name': 'Ready Driver',
    },
  },
  {
    destinationId,
    projectId,
    installRef: nanoid(),
    helloId: nanoid(),
    status: READY_TO_COLLECT,
    lastUpdateTime: dt(-5),
    lastTransitionTime: dt(-5),
    customerName: 'Ready Group 2',
    orderId: '100091',
    simphonyCheckNumber: 'no 91',
    isAcknowledged: false,
    hasNotified: [],
    orderActivationTime: dt(-10),
    partnerPickupTime: '2025-10-16T15:05:00.000Z',
    hsFields: {
      'Customer Name': 'Ready Group 2',
      'Order Number': '100091',
      'Collection Window': '15:05 - 15:35',
      'Mobile Number': '0424555002',
      'Car Model': 'Partner Van',
      'Car Color': 'White',
      'Car Plate': 'RDY001',
      'Parking Bay': '15',
      'Van Seq': 'Van 15 Seq 516',
      'Ambient': '3',
      'Chilled': '2',
      'Frozen': '1',
      'Order Type': 'Partner Delivery',
      'Driver Name': 'Ready Driver',
    },
  },
]

export const mockCompletedOrder: OrderDetails = {
  destinationId,
  projectId,
  installRef: nanoid(),
  helloId: nanoid(),
  status: 'Completed',
  lastUpdateTime: dt(-60),
  lastTransitionTime: dt(-60),
  waitTimeStart: dt(-65),
  customerName: 'Zara Completed',
  orderId: '100100',
  simphonyCheckNumber: 'no 100',
  isAcknowledged: true,
  hasNotified: [],
  orderActivationTime: dt(-70),
  hsFields: {
    'Customer Name': 'Zara Completed',
    'Order Number': '100100',
    'Collection Window': '08:00 - 08:30',
    'Mobile Number': '0411000000',
    'Car Model': 'Mazda',
    'Car Color': 'Red',
    'Car Plate': 'ZARA100',
    'Parking Bay': '10',
    'Van Seq': 'Van 99 Seq 199',
    'Order Type': 'Delivery',
    'Driver Name': 'Driver Completed',
  },
}

export const mockCancelledOrder: OrderDetails = {
  destinationId,
  projectId,
  installRef: nanoid(),
  helloId: nanoid(),
  status: 'Cancelled',
  lastUpdateTime: dt(-120),
  lastTransitionTime: dt(-120),
  waitTimeStart: dt(-125),
  customerName: 'Yasmin Cancelled',
  orderId: '100101',
  simphonyCheckNumber: 'no 101',
  isAcknowledged: false,
  hasNotified: [],
  orderActivationTime: dt(-130),
  hsFields: {
    'Customer Name': 'Yasmin Cancelled',
    'Order Number': '100101',
    'Collection Window': '07:00 - 07:30',
    'Mobile Number': '0411000001',
    'Car Model': 'Toyota',
    'Car Color': 'Blue',
    'Car Plate': 'YAZ101',
    'Parking Bay': '11',
    'Van Seq': 'Van 98 Seq 198',
    'Order Type': 'Delivery',
    'Driver Name': 'Driver Cancelled',
  },
}

export const mockAcknowledgedOrder: OrderDetails = {
  destinationId,
  projectId,
  installRef: nanoid(),
  helloId: nanoid(),
  status: 'Delivering',
  lastUpdateTime: dt(-30),
  lastTransitionTime: dt(-30),
  waitTimeStart: dt(-35),
  customerName: 'Alex Ack',
  orderId: '100102',
  simphonyCheckNumber: 'no 102',
  isAcknowledged: true,
  hasNotified: [],
  orderActivationTime: dt(-40),
  hsFields: {
    'Customer Name': 'Alex Ack',
    'Order Number': '100102',
    'Collection Window': '09:00 - 09:30',
    'Mobile Number': '0411000002',
    'Car Model': 'Ford',
    'Car Color': 'Black',
    'Car Plate': 'ACK102',
    'Parking Bay': '12',
    'Van Seq': 'Van 97 Seq 197',
    'Order Type': 'Delivery',
    'Driver Name': 'Driver Ack',
  },
}

export const mockOnTheWayOrders: OrderDetails[] = [
  mockOnTheWayOrder, // Single regular on-the-way order for tutorial Step 4
  mockOnTheWayOrder2,
  mockOnTheWayOrder3,
]

export const mockArrivedOrders: OrderDetails[] = [
  mockArrivedOrder,
  mockArrivedOrder2,
  mockArrivedOrder3,
]

/**
 * Delivering orders carrying restricted crate area codes (BD-8243).
 *
 * Coles marks restricted crates in `customEventMetaData.array_barcodes`
 * (`orderId*areaCode*crateNumber`). Area code 41 is tobacco (labelled "Restricted"
 * server-side) and 42 is liquor, per the area code mapping in
 * `tempo_screen_api/constants/areaCodeCrateTypeMapping`.
 *
 * `areaCodeCrateTypeMapping` is deliberately omitted: the backend strips it from the
 * response whenever Zebra barcode scanning is off for the store, and Proof of Age is
 * an independent toggle, so detection must work without it.
 */
export const mockLiquorOrder: OrderDetails = {
  ...mockDeliveringOrder,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100041',
  customerName: 'Larry Liquor',
  hsFields: {
    ...mockDeliveringOrder.hsFields,
    'Customer Name': 'Larry Liquor',
    'Order Number': '100041',
    'Car Plate': 'WINE041',
    'Van Seq': 'Van 41 Seq 141',
    'Restricted': 'Yes',
    'Order Type': 'Click & Collect',
    'Emojis': '🍷',
  },
  customEventMetaData: {
    array_barcodes: ['A100041*1*001', 'A100041*42*001'].join(','),
  },
}

export const mockTobaccoOrder: OrderDetails = {
  ...mockDeliveringOrder,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100042',
  customerName: 'Tina Tobacco',
  hsFields: {
    ...mockDeliveringOrder.hsFields,
    'Customer Name': 'Tina Tobacco',
    'Order Number': '100042',
    'Car Plate': 'TOBC042',
    'Van Seq': 'Van 42 Seq 142',
    'Restricted': 'Yes',
    'Order Type': 'Click & Collect',
    'Emojis': '🚬',
  },
  customEventMetaData: {
    array_barcodes: ['A100042*1*001', 'A100042*41*001'].join(','),
  },
}

export const mockLiquorAndTobaccoOrder: OrderDetails = {
  ...mockDeliveringOrder,
  helloId: nanoid(),
  installRef: nanoid(),
  orderId: '100043',
  customerName: 'Bothy McBoth',
  hsFields: {
    ...mockDeliveringOrder.hsFields,
    'Customer Name': 'Bothy McBoth',
    'Order Number': '100043',
    'Car Plate': 'BOTH043',
    'Van Seq': 'Van 43 Seq 143',
    'Restricted': 'Yes',
    'Order Type': 'Click & Collect',
    'Emojis': '🍷🚬',
  },
  customEventMetaData: {
    array_barcodes: ['A100043*1*001', 'A100043*41*001', 'A100043*42*001'].join(','),
  },
}

function createPoaScenarioOrder({
  orderId,
  customerName,
  categories,
  scenario,
}: {
  orderId: string
  customerName: string
  categories: Array<'liquor' | 'tobacco'>
  scenario: string
}): OrderDetails {
  const restrictedAreaCodes = categories.map(category => category === 'liquor' ? '42' : '41')
  const emojis = `${categories.includes('liquor') ? '🍷' : ''}${categories.includes('tobacco') ? '🚬' : ''}`
  const sequence = orderId.slice(-2)

  return {
    ...mockDeliveringOrder,
    helloId: nanoid(),
    installRef: nanoid(),
    orderId,
    customerName,
    hsFields: {
      ...mockDeliveringOrder.hsFields,
      'Customer Name': customerName,
      'Order Number': orderId,
      'Car Plate': `POA${sequence}`,
      'Van Seq': `Van ${sequence} Seq 1${sequence}`,
      'Restricted': 'Yes',
      'Order Type': 'Click & Collect',
      'Emojis': emojis,
      'POA Mock Scenario': scenario,
    },
    customEventMetaData: {
      array_barcodes: [
        `A${orderId}*1*001`,
        ...restrictedAreaCodes.map((areaCode, index) => `A${orderId}*${areaCode}*00${index + 1}`),
      ].join(','),
    },
  }
}

/**
 * One independent order for each row in the approved POA flow image. The answer
 * path is intentionally not pre-selected: use the customer name as the script
 * when exercising the flow, while Zustand persists each order's own draft.
 */
export const mockPoaMixedPassOrder = createPoaScenarioOrder({
  orderId: '100045',
  customerName: 'POA 1 Mixed Pass',
  categories: ['liquor', 'tobacco'],
  scenario: 'RSA pass, looks 25 or older',
})

export const mockPoaTobaccoPassOrder = createPoaScenarioOrder({
  orderId: '100046',
  customerName: 'POA 2 Tobacco Pass',
  categories: ['tobacco'],
  scenario: 'Tobacco only, looks 25 or older',
})

export const mockPoaIdPassOrder = createPoaScenarioOrder({
  orderId: '100047',
  customerName: 'POA 3 ID Pass',
  categories: ['liquor', 'tobacco'],
  scenario: 'RSA pass, check ID, accepted ID',
})

export const mockPoaIdFailureOrder = createPoaScenarioOrder({
  orderId: '100048',
  customerName: 'POA 4 ID Refusal',
  categories: ['liquor', 'tobacco'],
  scenario: 'RSA pass, failed ID, remove all restricted items',
})

export const mockPoaRsaRefusalOrder = createPoaScenarioOrder({
  orderId: '100049',
  customerName: 'POA 5 RSA Refusal',
  categories: ['liquor', 'tobacco'],
  scenario: 'RSA refusal, record reason, continue tobacco age check',
})

export const mockPoaTobaccoIdFailureOrder = createPoaScenarioOrder({
  orderId: '100050',
  customerName: 'POA 6 Tobacco Refusal',
  categories: ['tobacco'],
  scenario: 'Tobacco only, failed ID, remove tobacco',
})

export const mockPoaFlowOrders: OrderDetails[] = [
  mockPoaMixedPassOrder,
  mockPoaTobaccoPassOrder,
  mockPoaIdPassOrder,
  mockPoaIdFailureOrder,
  mockPoaRsaRefusalOrder,
  mockPoaTobaccoIdFailureOrder,
]

/** All restricted Delivering fixtures used by dashboard/mock API surfaces. */
export const mockPoaDeliveringOrders: OrderDetails[] = [
  mockLiquorOrder,
  mockTobaccoOrder,
  mockLiquorAndTobaccoOrder,
  ...mockPoaFlowOrders,
]

/** Delivering order with crates but no restricted area codes. */
export const mockUnrestrictedCrateOrder: OrderDetails = {
  ...mockDeliveringOrder,
  orderId: '100044',
  customerName: 'Norman Normal',
  customEventMetaData: {
    array_barcodes: ['A100044*1*001', 'A100044*31*001'].join(','),
  },
}

export const mockDeliveringOrders: OrderDetails[] = [
  mockDeliveringOrder,
  mockDeliveringOrder2,
  mockDeliveringOrder3,
]

export const mockOverdueOrders: OrderDetails[] = [
  mockOverdueOrder,
  mockOverdueOrder2,
  mockOverdueOrder3,
  mockOverdueOrder4,
]

export const mockReadyToCollectOrders: OrderDetails[] = [
  mockReadyToCollectOrder,
  mockReadyToCollectOrder2,
  mockReadyToCollectOrder3,
]

export const mockReadyToPickOrders: OrderDetails[] = [
  mockReadyToPickOrder,
  mockReadyToPickOrder2,
  mockReadyToPickOrder3,
]

export const mockRawOrders: OrderDetails[] = [
  mockOnTheWayOrder,
  mockArrivedOrder,
  mockDeliveringOrder,
  mockOverdueOrder,
  mockReadyToCollectOrder,
  mockReadyToPickOrder,
  mockCompletedOrder,
  mockCancelledOrder,
  mockAcknowledgedOrder,
]

export const mockOrdersByCategory = {
  onTheWay: mockOnTheWayOrders,
  arrived: mockArrivedOrders,
  delivering: [...mockDeliveringOrders, ...mockPoaDeliveringOrders],
  readyToPick: mockReadyToPickOrders,
  readyToCollect: mockReadyToCollectOrders,
  overdue: mockOverdueOrders,
}

export const mockOrdersBoard = {
  onTheWay: mockOnTheWayOrders,
  arrived: mockArrivedOrders,
  delivering: [...mockDeliveringOrders, ...mockPoaDeliveringOrders],
  readyToCollect: mockReadyToCollectOrders,
  readyToPick: mockReadyToPickOrders,
}

export const mockOrdersBoardWithGroupOrders = {
  onTheWay: [
    ...mockOnTheWayOrders,
    ...mockGroupPartnerOnTheWayOrders,
    ...mockGroupPartnerOrdersWithin15Min,
    ...mockNotGroupPartnerOrdersBeyond15Min,
    ...mockGroupPartnerOrdersExactly15Min,
  ],
  arrived: [...mockArrivedOrders, ...mockGroupArrivedOrders],
  delivering: [...mockDeliveringOrders, ...mockPoaDeliveringOrders, ...mockGroupDeliveringOrders],
  readyToCollect: [...mockReadyToCollectOrders, ...mockGroupReadyToCollectOrders],
  readyToPick: mockReadyToPickOrders,
  overdue: mockOverdueOrders,
}
